/**
 * Which invite link a programmer should be handing out, given who and what is in context.
 *
 * The rule the product wants: **combined wins whenever we know both an artist and a show**.
 * One link where the artist completes their profile and fills in the show details beats
 * two links every time, and programmers were defaulting to the single-purpose ones purely
 * because those were the only buttons that existed.
 *
 * Kept free of JSX so the precedence rules can be read — and tested — on their own.
 */

import {
    generateCombinedInviteLink,
    generateHappeningStubLink,
    generateInviteLink,
    type Happening,
    type Person,
} from '@littlebooker/dal';

/**
 * The DAL exposes two `Person` shapes: the mapped one that `listPeople` returns (with
 * `invite_token`) and the raw row that `getPerson` returns (with
 * `profile_completion_token`). The link builders want the former, but list rows can
 * only afford to fetch the latter one id at a time.
 *
 * This bridges the two for link-building purposes only — `slug`, `name` and the invite
 * token are everything `generateInviteLink` and `generateCombinedInviteLink` read.
 */
export const personFromRow = (row: {
    id: string;
    slug: string;
    name: string | null;
    email: string | null;
    profile_completion_token: string | null;
}): Person =>
    ({
        id: row.id,
        slug: row.slug,
        name: row.name,
        email: row.email ?? '',
        invite_token: row.profile_completion_token,
    }) as Person;

export type InviteLinkVariant = 'combined' | 'profile' | 'show';

/** What still needs a token minted before a link can exist. */
export type MintTarget = 'person' | 'happening' | 'both';

export interface InviteLinkOption {
    variant: InviteLinkVariant;
    /** Null when a token is missing — check `mintable` to see if a click could fix that. */
    url: string | null;
    label: string;
    /** Longer explanation, used as a title attribute or a disabled menu item's hint. */
    hint?: string;
    /** Set when `url` is null but generating a token would produce one. */
    mintable?: MintTarget | null;
}

export interface ResolvedInviteLinks {
    primary: InviteLinkOption;
    /** The single-purpose links, always present, disabled when their token is missing. */
    others: InviteLinkOption[];
}

const hasStubToken = (happening: Happening | null | undefined) =>
    Boolean(happening?.metadata?.stub_token);

const profileOption = (
    person: Person | null | undefined,
    origin: string,
): InviteLinkOption => {
    if (!person) {
        return {
            variant: 'profile',
            url: null,
            label: 'Profile-only link',
            hint: 'No artist in context.',
            mintable: null,
        };
    }
    const url = generateInviteLink(person, origin);
    return {
        variant: 'profile',
        url,
        label: 'Profile-only link',
        hint: url ? undefined : 'This person has no invite link yet',
        mintable: url ? null : 'person',
    };
};

const showOption = (
    happening: Happening | null | undefined,
    origin: string,
): InviteLinkOption => {
    if (!happening) {
        return {
            variant: 'show',
            url: null,
            label: 'Show-only link',
            hint: 'No show in context.',
            mintable: null,
        };
    }
    const url = generateHappeningStubLink(happening, origin);
    return {
        variant: 'show',
        url,
        label: 'Show-only link',
        hint: url ? undefined : 'No artist link on this show yet',
        mintable: url ? null : 'happening',
    };
};

/** What would have to be minted for a combined link to exist for this pair. */
const combinedMintTarget = (person: Person, happening: Happening): MintTarget | null => {
    const needsPerson = !person.invite_token;
    const needsShow = !hasStubToken(happening);
    if (needsPerson && needsShow) return 'both';
    if (needsPerson) return 'person';
    if (needsShow) return 'happening';
    return null;
};

/**
 * Resolve the link a copy button should offer, plus the alternatives behind its menu.
 *
 * Combined takes precedence whenever both a person and a happening are present — even if
 * a token is missing. In that case `url` is null and `mintable` says what to generate, so
 * the button can mint-and-copy in one click rather than sending the programmer off to
 * hunt for a checkbox. Falling back to a single-purpose link because a token happened to
 * be absent is exactly the behaviour this is meant to end.
 */
export const resolveInviteLinks = (
    person: Person | null | undefined,
    happening: Happening | null | undefined,
    origin: string,
): ResolvedInviteLinks => {
    const others = [profileOption(person, origin), showOption(happening, origin)];

    if (person && happening) {
        const url = generateCombinedInviteLink(person, happening, origin);
        return {
            primary: {
                variant: 'combined',
                url,
                label: 'Copy invite link',
                mintable: url ? null : combinedMintTarget(person, happening),
            },
            others,
        };
    }

    if (person) {
        return {
            primary: {
                ...profileOption(person, origin),
                label: 'Copy profile link',
                hint: 'No upcoming show with an artist link yet — this link only covers their profile.',
            },
            others,
        };
    }

    if (happening) {
        return {
            primary: {
                ...showOption(happening, origin),
                label: 'Copy show link',
                hint: 'No artist on this show yet — this link only covers the show details.',
            },
            others,
        };
    }

    return {
        primary: {
            variant: 'combined',
            url: null,
            label: 'Copy invite link',
            hint: 'Nothing to link to yet.',
            mintable: null,
        },
        others,
    };
};

/**
 * Pick the show a person's invite link should point at.
 *
 * A person has no single show, so the combined link needs one chosen for them. The
 * soonest upcoming show is the one a programmer is almost always chasing an artist
 * about; a show that already has a stub token wins over one that doesn't, so we reuse
 * an existing link rather than minting a second credential for the same artist.
 *
 * Returns null when the person has no upcoming show at all — callers should degrade to
 * the profile-only link rather than reaching backwards to a show that already happened.
 */
export const pickCounterpartShow = (
    happenings: Happening[],
    now: Date = new Date(),
): Happening | null => {
    const upcoming = happenings
        .filter((h) => h.start_time && new Date(h.start_time) >= now)
        .sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? ''));
    if (upcoming.length === 0) return null;
    return upcoming.find((h) => h.metadata?.stub_token) ?? upcoming[0];
};

/** The next few upcoming shows, for offering alternates behind the menu. */
export const upcomingShowsFor = (
    happenings: Happening[],
    limit = 3,
    now: Date = new Date(),
): Happening[] =>
    happenings
        .filter((h) => h.start_time && new Date(h.start_time) >= now)
        .sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? ''))
        .slice(0, limit);

/**
 * The artist a show's invite link belongs to.
 *
 * A show carries one stub token and whoever holds it can edit the whole show, so the
 * combined link goes to a single designated filler — by convention the first artist on
 * the bill, which is the same one the 'separate' email strategy has always mailed the
 * stub link to. Co-artists get profile-only links instead.
 */
export const pickShowFillerArtist = <T extends { person_id: string; sort_order?: number }>(
    artists: T[],
): T | null => {
    const withPerson = artists.filter((artist) => artist.person_id);
    if (withPerson.length === 0) return null;
    return [...withPerson].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0];
};
